import { Injectable, Optional } from '@angular/core';
import {
  throwError as observableThrowError,
  Observable,
  throwError,
} from 'rxjs';
import { map, catchError, timeout, switchMap, retry } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { ApiResponse, ApiError, RawApiResponse } from './api-response';
import { LocalDataStorageService } from './local-data-storage.service';
import { TransferState } from '@angular/core';
import * as P from 'pino';
import { LogginService } from './logging/logging-provider';
import { SsrService } from './ssr.service';
import { HrraApiHeaders, HrraUrlParams } from './api-headers';
import { EnvService } from './env.service';
import { generateHash } from './utils/hash';
import { AwsWafTokenService } from './aws-waf.service';
import { HttpClientConfig } from './http-client-config';
import opentelemetry, { SpanStatusCode } from '@opentelemetry/api';

@Injectable({
  providedIn: 'root',
})
export class SmartHttpService {
  // SERVER_TIMEOUT = 5000;
  // BROWSER_TIMEOUT = 60000;
  // RETRY_COUNT = 3;

  //hardcoded defaults
  httpClientConfig: HttpClientConfig = {
    serverTimeout: 5000,
    browserTimeout: 60000,
    retryCount: 3
  };

  constructor(
    private http: HttpClient,
    private storage: LocalDataStorageService,
    @Optional() private trasferState: TransferState,
    private loggingService: LogginService,
    private ssrService: SsrService,
    private envService: EnvService,
    private wafTokenService: AwsWafTokenService
  ) {
    this._logger = this.loggingService.createLogger('smart-http-service');
  }

  private _logger: P.Logger;
  //TODO add auth headers and localization params (language)

  get logger() {
    return this._logger;
  }

  public loadConfig(httpClientConfig: HttpClientConfig){
    if(httpClientConfig === undefined || httpClientConfig === null) {
      this.logger.warn('HttpClientConfig is not provided, using default values');
    }
    
    this.httpClientConfig = httpClientConfig || this.httpClientConfig;
  }

  isWafTokenRequired(){
    return this.ssrService.isPlatformBrowser() && this.envService.wafConfig && this.envService.wafConfig.isEnabled;
  }

  public get<TData, TError = {}>(
    url: string,
    params: { [index: string]: any },
    withCredentials = true,
    includeAuthToken = true
  ): Observable<ApiResponse<TData, TError>> {

    this.logger.debug({ url, params }, 'Http request, url: %s params: %o', url, params);

    if (url.startsWith('EMPTY_URL_PLACEHOLDER')) {
      this.logger.fatal(
        { url: url, params: params },
        'API URL placeholder not replaced'
      );
    }

    if (this.trasferState !== null) {
      if (this.ssrService.isPlatformBrowser()) {
        this.logger.trace(
          { state: JSON.parse(this.trasferState.toJson()) },
          'TRANSFER STATE'
        );
      }
    } else {
      this.logger.warn({}, 'Transfer state provider not configured');
    }

    let searchParams = new HttpParams();

    for (let p in params) {
      if (params.hasOwnProperty(p) && params[p]) {
        if (params[p] instanceof Array) {
          params[p].forEach((item) => {
            searchParams = searchParams.append(p, item);
          });
        } else {
          searchParams = searchParams.append(p, params[p]);
        }
      }
    }

    const requestTimeout = this.ssrService.isPlatformServer() ? this.httpClientConfig.serverTimeout : this.httpClientConfig.browserTimeout;

    let start = performance.now();

    let headers = new HttpHeaders();

    if (this.ssrService.isPlatformServer()) {
      if (this.envService.cacheConfig.isApiCacheEnabled) {
        headers = headers.append(HrraApiHeaders.InternalApiCaching, 'true');
      }

      if (this.envService.cacheConfig.isApiCompressionEnabled) {
        headers = headers.append(HrraApiHeaders.InternalApiCompression, 'true');
      }
    }

    //this.logger.debug('App version: ', this.envService.appVersion);

    searchParams = searchParams.append(HrraUrlParams.CacheBuster, generateHash(this.envService.appVersion))

    return this.getRaw<ApiResponse<TData, TError>>(
      url,
      headers,
      searchParams,
      withCredentials,
      includeAuthToken
    ).pipe(
      timeout(requestTimeout),
      map((d) => {
        let end = performance.now();
        this.logger.debug({ url: url, params: params, duration: end - start }, 'Http request duration');
        return this.extractData<TData, TError>(d)
      }),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  /**
   * Fetches plain text/html content (e.g. static html files from assets) instead of json api response.
   * Response body is returned as is in `data` property.
   */
  public getHtml<TError = {}>(
    url: string,
    params: { [index: string]: any } = {},
    withCredentials = false,
    includeAuthToken = false
  ): Observable<ApiResponse<string, TError>> {
    this.logger.debug({ url, params }, 'Http html request, url: %s params: %o', url, params);

    let searchParams = new HttpParams();

    for (let p in params) {
      if (params.hasOwnProperty(p) && params[p]) {
        searchParams = searchParams.append(p, params[p]);
      }
    }

    searchParams = searchParams.append(HrraUrlParams.CacheBuster, generateHash(this.envService.appVersion));

    let headers = new HttpHeaders({ Accept: 'text/html, text/plain, */*' });

    if (includeAuthToken && this.storage.hasAccessToken()) {
      headers = headers.append('Authorization', `Bearer ${this.storage.getAccessToken()}`);
    }

    if (this.storage.isUiLanguageSet()) {
      headers = headers.append('Accept-Language', this.storage.getUiCulture());
    }

    const requestTimeout = this.ssrService.isPlatformServer() ? this.httpClientConfig.serverTimeout : this.httpClientConfig.browserTimeout;

    const http$ = this.http.get(url, {
      headers: headers,
      observe: 'response',
      params: searchParams,
      responseType: 'text',
      withCredentials: withCredentials,
    });

    const request$ = this.isWafTokenRequired()
      ? this.wafTokenService.getToken().pipe(
          retry(this.httpClientConfig.retryCount),
          switchMap(() => http$)
        )
      : http$;

    return request$.pipe(
      timeout(requestTimeout),
      map((response) => {
        if (response.status !== 200) {
          throw new HttpErrorResponse({
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            headers: response.headers,
          });
        }

        return <ApiResponse<string, TError>>{
          success: true,
          error: null,
          status: response.status,
          statusText: response.statusText,
          data: response.body,
        };
      }),
      catchError((err) => observableThrowError(this.processRawError<string, TError>(err)))
    );
  }

  public post<TData, TError = {}>(
    url: string,
    data: any,
    withCredentials = true
  ): Observable<ApiResponse<TData, TError>> {
    let body = JSON.stringify(data);
    let headers = new HttpHeaders({ 'Content-type': 'application/json' });

    const requestTimeout = this.ssrService.isPlatformServer()
      ? this.httpClientConfig.serverTimeout
      : this.httpClientConfig.browserTimeout;

    return this.postRaw<ApiResponse<TData, TError>>(
      url,
      body,
      headers,
      null,
      withCredentials
    ).pipe(
      timeout(requestTimeout),
      map((d) => this.extractData<TData, TError>(d)),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  public sendBeacon(url: string, data: FormData): boolean {
    return navigator.sendBeacon(url, data);
  }

  public postWithParams<TData, TError = {}>(
    url: string,
    params: { [index: string]: any },
    withCredentials = true
  ): Observable<ApiResponse<TData, TError>> {
    let searchParams = new HttpParams();

    for (let p in params) {
      if (params.hasOwnProperty(p) && params[p]) {
        if (params[p] instanceof Array) {
          params[p].forEach((item) => {
            searchParams = searchParams.append(p, item);
          });
        } else {
          searchParams = searchParams.append(p, params[p]);
        }
      }
    }

    const requestTimeout = this.ssrService.isPlatformServer()
      ? this.httpClientConfig.serverTimeout
      : this.httpClientConfig.browserTimeout;

    return this.postRaw<ApiResponse<TData, TError>>(
      url,
      null,
      null,
      searchParams,
      withCredentials
    ).pipe(
      timeout(requestTimeout),
      map((d) => this.extractData<TData, TError>(d)),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  public postWithFile<TData, TError = {}>(
    url: string,
    data: FormData,
    withCredentials = true
  ): Observable<ApiResponse<TData, TError>> {
    let headers = new HttpHeaders();

    return this.postRaw<ApiResponse<TData, TError>>(
      url,
      data,
      headers,
      null,
      withCredentials
    ).pipe(
      map((d) => this.extractData<TData, TError>(d)),
      catchError((err) => {
        let processedError = this.processRawError(err);
        return observableThrowError(processedError);
      })
    );
  }

  private processRawError<TData, TError>(
    rawError: any
  ): ApiResponse<TData, TError> {
    let result = <ApiResponse<TData, TError>>{
      success: false,
      data: null,
      error: <ApiError<TError>>{},
    };

    if (rawError instanceof HttpErrorResponse) {
      let error = <HttpErrorResponse>rawError;

      result.status = error.status;
      result.statusText = error.statusText;

      result.error.isServerError = error.status == 500;
      result.error.isUnauthorized = error.status == 401;
      result.error.isPermissionDenied = error.status == 403;
      result.error.isClientError = error.status == 400 || error.status == 404;
      result.error.isConnectionFailure = error.status == 0;

      //TODO: result.error.isSessionExpired = error.status == 440;

      if (this.isJson(error)) {
        let er = error.error;
        result.error.errorCode = er.error.errorCode;
        result.error.errorMessage = er.error.errorMessage;
        result.error.errorReferenceId = er.error.errorReferenceId;
        result.error.errorDetails = er.error.errorDetails;
        result.error.additionalInfo = er.error.additionalInfo;
      }

      this.logger.error(
        {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          message: error.message,
        },
        'ERROR in http request'
      );
    } else {
      result.status = 0; //connection error
      result.error.errorMessage = "Connection error (client error)"
      this.logger.error(rawError, 'Uknown error in http request');
      //this.logger.error(rawError);
    }

    if(this.ssrService.isPlatformServer()){
      const currentSpan = opentelemetry.trace.getActiveSpan();
      this.logger.debug({ spanId: currentSpan?.spanContext().spanId, traceId: currentSpan?.spanContext().traceId }, 'Setting span status to error')
      currentSpan?.setStatus({ code: SpanStatusCode.ERROR, message: result.error?.errorMessage });
      this.ssrService.setStatusInternalServerError();
    }

    return result;
  }

  private extractData<TData, TError>(
    response: HttpResponse<RawApiResponse<TData>>
  ): ApiResponse<TData, TError> {
    this.logger.trace('Success response from server, extracting data...');
    let result = <ApiResponse<TData, TError>>{
      success: true,
      error: null,
      status: response.status,
      statusText: response.statusText,
      data: null,
    };

    if (this.hasContent(response)) {
      if (this.isJson(response)) {
        if (response.body === null || response.body === undefined) {
          this.logger.error({ response }, 'Response status: %s, statusText: %s, url: %s, headers: ', response.status, response.statusText, response.url, response.headers);
        }
        result.data = this.preprocessResponseData(response.body.data);
      } else {
        throw new Error('Invalid response contents type');
      }
    }

    return result;
  }

  private preprocessResponseData<TData>(data: TData) {
    /*
        Modifying angular http client response object (reference returned by http client) when using angular universal (ssr)
        and http transfer state causes issues: browser receives already modified object via http transfer state and same changes
        are applied again.
        This happens because when running on server, modifications are applied to original object returned by http client, 
        then used by http transfer state to send to browser as a cached response.
        As a result same changes are applied two times, for example adding some item to array property in response object
        gives us array with that item appended twice (once appended when running on server and once when running in browser).
        As a workaround when running on server we clone original response object before modifying it, to leave original object
        untouched, so that http transfer state can use it as a cached response.
        */
    if (this.ssrService.isPlatformServer()) {
      return JSON.parse(JSON.stringify(data));
    } else if (this.ssrService.isPlatformBrowser()) {
      return data;
    } else {
      throw new Error('Unsupported platform');
    }
  }

  private isJson<TData, TError>(
    response: HttpResponse<RawApiResponse<TData>> | HttpErrorResponse
  ) {
    let contentType = response.headers.get('content-type');
    return (
      contentType &&
      (contentType.indexOf('application/json') !== -1 ||
        contentType.indexOf('application/problem+json') !== -1)
    );
  }

  private hasContent<TData>(
    response: HttpResponse<RawApiResponse<TData>> | HttpErrorResponse
  ) {
    return response.status != 204; //TODO enum for http statuses
  }

  public getRaw<T>(
    url: string,
    headers?: HttpHeaders,
    params?: HttpParams,
    withCredentials = false,
    includeAuthToken = false
  ): Observable<HttpResponse<T>> {
    if (headers === undefined || headers === null) {
      headers = new HttpHeaders();
    }

    if (includeAuthToken && this.storage.hasAccessToken()) {
      headers = headers.append(
        'Authorization',
        `Bearer ${this.storage.getAccessToken()}`
      );
    }

    if (this.storage.isUiLanguageSet()) {
      headers = headers.append('Accept-Language', this.storage.getUiCulture());
    }

    this.logger.debug({ actualUrl: url }, 'ACTUAL URL: ' + url);
    
    const http$ = this.http.get<T>(url, {
        headers: headers,
        observe: 'response',
        params: params,
        responseType: 'json',
        withCredentials: withCredentials,
      });
    
    if( this.isWafTokenRequired()) {
      return this.wafTokenService.getToken().pipe(
        retry(this.httpClientConfig.retryCount),
        switchMap(token => {
          this.logger.debug({ token }, 'WAF Token received');
        return http$;
      }));
    }
    else {
      return http$;
    }
  }

  public postRaw<T>(
    url: string,
    body: any,
    headers?: HttpHeaders,
    params?: HttpParams,
    withCredentials = false
  ): Observable<HttpResponse<T>> {
    if (headers === undefined || headers === null) {
      headers = new HttpHeaders();
    }

    if (this.storage.hasAccessToken()) {
      headers = headers.append(
        'Authorization',
        `Bearer ${this.storage.getAccessToken()}`
      );
    }

    if (this.storage.isUiLanguageSet()) {
      headers = headers.append('Accept-Language', this.storage.getUiCulture());
    }

    const http$ = this.http.post<T>(url, body, {
      headers: headers,
      observe: 'response',
      params: params,
      responseType: 'json',
      withCredentials: withCredentials,
    });


    if( this.isWafTokenRequired()) {
      return this.wafTokenService.getToken().pipe(
        retry(this.httpClientConfig.retryCount),
        switchMap(token => {
          this.logger.debug({ token }, 'WAF Token received');
        return http$;
      }));
    }
    else {
      return http$;
    }
  }
}
